import type { FC, ReactNode } from 'react';
import { createElement, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import type { FallbackProps } from 'react-error-boundary';

type BaseProps = {
  url: string;
  scope: string;
  module: string;
  customLoader?: ReactNode;
};

type TProps<T = Record<string, unknown>> = BaseProps & T;

const ErrorFallback: FC<FallbackProps> = ({ error }) => (
  <code>{error instanceof Error ? error.message : String(error)}</code>
);

const RemoteComponentClient = <T extends object>({ url, scope, module, customLoader, ...props }: TProps<T>) => {
  const [Component, setComponent] = useState<FC<any> | null>(null);
  const [federation, setFederation] = useState<any>(null);

  useEffect(() => {
    import('./config').then((module) => {
      setFederation(module.federation);
    });
  }, []);

  useEffect(() => {
    if (!federation) {
      return;
    }

    (async function () {
      try {
        if (!federation.moduleCache.has(scope)) {
          federation.registerRemotes([{ entry: url, name: scope, alias: scope, shareScope: 'default' }], {
            force: true,
          });
        }

        const LoadedComponent = (await federation.loadRemote(`${scope}/${module}`)) as { default: FC<any> };
        setComponent(() => LoadedComponent.default);
      } catch (error) {
        console.error(`Error loading remote component ${scope}/${module}:`, error);
      }
    })();
  }, [federation, scope, module, url]);

  if (!Component) {
    return (customLoader as any) || <div className="remote-component-loader">loading</div>;
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      {createElement(Component, props as any)}
    </ErrorBoundary>
  );
};

// Removed next/dynamic, using simple client component directly as we are in SPA
const RemoteComponentRuntime = <T extends object>(props: TProps<T>) => {
  return <RemoteComponentClient {...props} />;
};

export default RemoteComponentRuntime;
